import React, { useState } from 'react';
import { 
  Eye, Scan, Brain, Activity, ShieldAlert, Navigation2, UserCheck,
  AlertTriangle, ArrowRight, ChevronDown, ChevronUp,
  TrendingUp, Clock, Heart, Layers, Target, BarChart3, Zap, Users
} from 'lucide-react';

const AGENTS = [
  {
    name: 'Sentinel Agent',
    description: 'Predicts DR/DME risk using imaging and systemic biomarkers before severe vision loss occurs.',
    icon: ShieldAlert,
    color: 'rose',
  },
  {
    name: 'Risk ID Agent',
    description: 'Measures retinal fluid burden and establishes personalized "Dry Weight" baselines for each patient.',
    icon: Target,
    color: 'sky',
  },
  {
    name: 'Pivot Agent',
    description: 'Detects inadequate treatment response early and alerts clinicians when therapy changes may be required.',
    icon: Zap,
    color: 'amber',
  },
  {
    name: 'Navigation Agent',
    description: 'Optimizes treatment intervals using continuous retinal response monitoring.',
    icon: Navigation2,
    color: 'emerald',
  },
  {
    name: 'Adherence Agent',
    description: 'Predicts patient dropout risk and improves follow-up compliance using personalized progress communication.',
    icon: UserCheck,
    color: 'violet',
  },
];

const BENEFITS = [
  { title: 'Earlier Detection', desc: 'Detect disease progression before major structural damage occurs.', icon: Eye },
  { title: 'Personalized Treatment', desc: 'Move beyond one-size-fits-all injection schedules.', icon: Users },
  { title: 'Quantified Monitoring', desc: 'Track retinal fluid and response dynamics with objective measurements.', icon: BarChart3 },
  { title: 'Faster Clinical Decisions', desc: 'AI-generated biomarker analysis supports faster treatment pivots.', icon: Zap },
  { title: 'Continuous Care', desc: 'Monitor disease progression longitudinally across every patient visit.', icon: Activity },
  { title: 'Reduced Vision Loss', desc: 'Identify recurrence and treatment failure earlier.', icon: Heart },
];

const WORKFLOW_STEPS = [
  { step: 1, title: 'Scan Capture', desc: 'Fundus and OCT scans are captured at the clinic.' },
  { step: 2, title: 'Data Ingestion', desc: 'SkyDoc securely ingests imaging and biomarker data.' },
  { step: 3, title: 'AI Analysis', desc: 'AI performs retinal segmentation and fluid quantification.' },
  { step: 4, title: 'Biomarker Scoring', desc: 'Biomarker scores and progression analysis are generated.' },
  { step: 5, title: 'Treatment Insights', desc: 'Doctors receive treatment support insights in real time.' },
  { step: 6, title: 'Patient Tracking', desc: 'Patients receive longitudinal progress tracking and follow-up guidance.' },
];

const COMPARISON = {
  traditional: [
    'Subjective scan interpretation',
    'Manual comparison across visits',
    'Fixed treatment schedules',
    'Delayed recognition of non-response',
  ],
  skydoc: [
    'AI-powered voxel analysis',
    'Longitudinal retinal mapping',
    'Predictive treatment intelligence',
    'Dynamic response-based care pathways',
  ],
};

const BIOMARKERS = [
  'Intraretinal fluid',
  'Subretinal fluid',
  'DRIL disorganization',
  'Hyperreflective inflammatory foci',
  'Retinal traction',
  'Neovascularization',
];

const getAgentColor = (color, isDark) => {
  const map = {
    rose:    { bg: isDark ? 'bg-rose-900/30' : 'bg-rose-50',       icon: 'text-rose-500',    border: isDark ? 'border-rose-800' : 'border-rose-200' },
    sky:     { bg: isDark ? 'bg-sky-900/30' : 'bg-sky-50',         icon: 'text-sky-500',     border: isDark ? 'border-sky-800' : 'border-sky-200' },
    amber:   { bg: isDark ? 'bg-amber-900/30' : 'bg-amber-50',     icon: 'text-amber-500',   border: isDark ? 'border-amber-800' : 'border-amber-200' },
    emerald: { bg: isDark ? 'bg-emerald-900/30' : 'bg-emerald-50', icon: 'text-emerald-500', border: isDark ? 'border-emerald-800' : 'border-emerald-200' },
    violet:  { bg: isDark ? 'bg-violet-900/30' : 'bg-violet-50',   icon: 'text-violet-500',  border: isDark ? 'border-violet-800' : 'border-violet-200' },
  };
  return map[color];
};

const DRDMESection = ({ isDark, onDemoClick }) => {
  const [expandedAgent, setExpandedAgent] = useState(null);

  return (
    <section
      id="dr-dme"
      className={`${isDark ? 'bg-slate-900' : 'bg-[#FAFAF9]'}`}
      data-testid="dr-dme-section"
    >
      {/* Hero */}
      <div className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-gradient-to-b from-sky-50 to-white'}`}>
        <div className="max-w-6xl mx-auto text-center">
          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>
            <Eye className="w-4 h-4" />
            DR & DME Precision Care
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI-Powered Precision Care for<br />
            <span className="text-sky-500">Diabetic Retinopathy & DME</span>
          </h2>
          <p className={`text-base lg:text-lg max-w-3xl mx-auto mb-8 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            SkyDoc transforms fragmented retinal imaging and diabetic care data into real-time clinical intelligence — helping doctors detect disease earlier, personalize treatment, and continuously track response over time.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={onDemoClick}
              className="px-6 py-3 bg-sky-500 text-white text-sm font-semibold rounded-full hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/25"
              data-testid="dr-dme-demo-btn"
            >
              Request Demo
            </button>
            <a
              href="#simulation"
              className={`px-6 py-3 text-sm font-semibold rounded-full border transition-all ${
                isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-white'
              }`}
              data-testid="dr-dme-workflow-btn"
            >
              See Clinical Workflow
            </a>
          </div>
        </div>
      </div>

      {/* Clinical Problem */}
      <div className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
                isDark ? 'bg-rose-900/40 text-rose-400' : 'bg-rose-100 text-rose-700'
              }`}>The Clinical Problem</span>
              <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                The Silent Vision Crisis
              </h3>
              <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Diabetic Retinopathy (DR) is one of the leading causes of preventable blindness in diabetic patients. The disease often progresses silently until irreversible retinal damage has already occurred.
              </p>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Diabetic Macular Edema (DME), the leading cause of vision loss in diabetics, requires continuous monitoring and long-term treatment. Yet current care pathways remain fragmented.
              </p>
            </div>
            <div className="space-y-3">
              {[
                'Fundus imaging and OCT scans analyzed separately',
                'Doctors rely heavily on manual interpretation',
                'Treatment response may take months to evaluate',
                'Small fluid changes are difficult to quantify',
                'Patients frequently drop out of treatment cycles',
                'Clinical decisions are reactive, not predictive',
              ].map((item, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${
                  isDark ? 'bg-slate-800' : 'bg-rose-50/60'
                }`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-rose-400' : 'text-rose-500'}`} />
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* What SkyDoc Does */}
      <div className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>The Solution</span>
          <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Agentic AI for Continuous Retina Care
          </h3>
          <p className={`text-sm max-w-2xl mx-auto mb-10 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            SkyDoc is an AI-powered precision retina platform designed for continuous DR and DME management, converting disconnected data points into a unified clinical intelligence layer.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { icon: Eye, label: 'Fundus Imaging' },
              { icon: Scan, label: 'OCT Scans' },
              { icon: Activity, label: 'Clinical Biomarkers' },
              { icon: BarChart3, label: 'Lab Values (HbA1c, BP)' },
              { icon: Clock, label: 'Treatment History' },
              { icon: Users, label: 'Adherence Patterns' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`p-4 rounded-xl border ${
                  isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200'
                }`}>
                  <Icon className={`w-5 h-5 mx-auto mb-2 ${isDark ? 'text-sky-400' : 'text-sky-500'}`} />
                  <p className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Technology Section */}
      <div className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'bg-violet-900/40 text-violet-400' : 'bg-violet-100 text-violet-700'
            }`}>Technology</span>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              Multimodal Retinal Intelligence
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                title: 'Fundus + OCT Fusion',
                desc: 'Combines surface retinal imaging with deep structural OCT analysis to create a complete retinal health profile.',
                icon: Layers,
              },
              {
                title: 'Voxel-Level Fluid Analysis',
                desc: 'Quantifies microscopic fluid volume inside retinal layers using AI-powered 3D voxel analysis — going beyond simple thickness measurements.',
                icon: Scan,
              },
              {
                title: 'Longitudinal Scan Registration',
                desc: 'AI aligns scans across visits to detect even tiny changes in swelling and disease activity over time.',
                icon: TrendingUp,
              },
              {
                title: 'Predictive Treatment Intelligence',
                desc: 'Tracks treatment response velocity — identifying fast responders, slow responders, non-responders, recurrence risk, and treatment plateau patterns.',
                icon: Brain,
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`p-6 rounded-2xl border ${
                  isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
                    isDark ? 'bg-violet-900/40' : 'bg-violet-100'
                  }`}>
                    <Icon className="w-5 h-5 text-violet-500" />
                  </div>
                  <h4 className={`text-base font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</h4>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Biomarkers */}
          <div className="mt-8">
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Critical Biomarkers Detected
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {BIOMARKERS.map((b, i) => (
                <span key={i} className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  isDark ? 'bg-slate-800 text-violet-400 border border-slate-700' : 'bg-violet-50 text-violet-700'
                }`}>
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Agents */}
      <div className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
            }`}>AI Agents</span>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              The SkyDoc Agentic Care System
            </h3>
            <p className={`text-sm max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Five specialized AI agents working in concert for continuous retinal monitoring and precision care.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AGENTS.map((agent, i) => {
              const Icon = agent.icon;
              const colors = getAgentColor(agent.color, isDark);
              const isExpanded = expandedAgent === i;
              return (
                <button
                  key={i}
                  onClick={() => setExpandedAgent(isExpanded ? null : i)}
                  className={`text-left p-5 rounded-2xl border transition-all ${colors.bg} ${colors.border} ${
                    isExpanded ? 'ring-2 ring-sky-500/40' : ''
                  }`}
                  data-testid={`dr-dme-agent-${i}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                  <h4 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{agent.name}</h4>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'} ${
                    isExpanded ? '' : 'line-clamp-2'
                  }`}>
                    {agent.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Clinical Benefits */}
      <div className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
            }`}>Benefits</span>
            <h3 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              Precision Retina Care
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className={`p-5 rounded-2xl border ${
                  isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
                }`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${
                    isDark ? 'bg-emerald-900/40' : 'bg-emerald-50'
                  }`}>
                    <Icon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <h4 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{b.title}</h4>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Clinical Workflow Timeline */}
      <div className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
            }`}>Workflow</span>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              From Scan to Decision Support
            </h3>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className={`absolute left-6 top-0 bottom-0 w-px ${isDark ? 'bg-slate-700' : 'bg-slate-200'} hidden md:block`} />

            <div className="space-y-4">
              {WORKFLOW_STEPS.map((ws) => (
                <div key={ws.step} className="flex items-start gap-4 md:gap-6">
                  <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    isDark ? 'bg-sky-900/50 text-sky-400 border border-sky-800' : 'bg-sky-100 text-sky-700'
                  }`}>
                    {ws.step}
                  </div>
                  <div className={`flex-1 p-4 rounded-xl ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                    <h4 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{ws.title}</h4>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{ws.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison: Traditional vs SkyDoc */}
      <div className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              From Observation to Quantification
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h4 className={`text-sm font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Traditional Care
              </h4>
              <div className="space-y-3">
                {COMPARISON.traditional.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? 'bg-slate-600' : 'bg-slate-400'}`} />
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 text-white">
              <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-sky-200">
                SkyDoc Precision Care
              </h4>
              <div className="space-y-3">
                {COMPARISON.skydoc.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-sky-300" />
                    <span className="text-sm text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Patient Engagement */}
      <div className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
                isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
              }`}>Patient Engagement</span>
              <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                Keeping Patients Connected to Care
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Patients with chronic retinal disease often stop treatment because they cannot see progress. SkyDoc improves engagement through visual progress tracking, personalized recovery reports, and automated follow-up workflows.
              </p>
            </div>
            <div className="space-y-3">
              {[
                'Visual progress tracking',
                'Personalized recovery reports',
                'Automated follow-up workflows',
                'Treatment timeline visibility',
                'Progress-based adherence reminders',
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${
                  isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'
                }`}>
                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  </div>
                  <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Future Vision */}
      <div className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>Future</span>
          <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Beyond the Retina
          </h3>
          <p className={`text-sm max-w-2xl mx-auto mb-8 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            The retina reflects systemic vascular health. SkyDoc's multimodal architecture is being designed to expand toward broader clinical intelligence.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Cardio-metabolic risk detection', 'Renal disease signals', 'Neurodegenerative biomarkers', 'Chronic disease intelligence'].map((item, i) => (
              <span key={i} className={`px-4 py-2 rounded-full text-xs font-medium border ${
                isDark ? 'border-sky-800 text-sky-400 bg-sky-900/20' : 'border-sky-200 text-sky-700 bg-sky-50'
              }`}>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className={`py-12 px-4 ${isDark ? 'bg-slate-800' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
        <div className="max-w-3xl mx-auto text-center">
          <h3 className={`text-xl sm:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to transform diabetic eye care?
          </h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            See how SkyDoc's retinal intelligence platform can improve outcomes at your facility.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={onDemoClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 text-white text-sm font-semibold rounded-full hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/25"
              data-testid="dr-dme-bottom-demo-btn"
            >
              Request a Demo
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#simulation"
              className={`px-6 py-3 text-sm font-semibold rounded-full border transition-all ${
                isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
            >
              Explore Live Demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DRDMESection;
